import React, { useEffect, useState } from 'react'

import { ModalHeader, ModalBody } from '../../Modal/Modal'
import Search from '../../Search/Search'
import Accordion from '../../Accordion/Accordion'
import Icon from '../../Icon2/Icon'
import { Truncate } from '../../Truncate/Truncate'
import { getCtUserDefinedFields } from './util'
import Tooltip from '../../Tooltip/Tooltip'
import InfiniteScroll from 'react-infinite-scroll-component'
import SkeletonTile from '../../SkeletonTile/SkeletonTile'
import './CustomFields.css'

const LoaderV2 = ({ }) => {
  return (
    <div
      className="Table__loader"
      data-test-id="cs-table-loader"
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        background: '#fff',
        zIndex: 99
      }}>
      <div className="Table__loader_content">
        <Icon icon="PurpleLoader" className="Table__loader-svg" />
        <div className="Table__loader-desc">Fetching details...</div>
      </div>
    </div>
  )
}

const SkeletonLoader = ({ size = 2, tileleftSpace = 18, tileWidth = 250 }): React.JSX.Element => {
  return (
    <div>
      {Array(size)
        .fill(1)
        .map((data, index) => {
          return (
            <div className="flex-v-center" key={index}>
              <SkeletonTile
                numberOfTiles={1}
                tileHeight={12}
                tileWidth={tileWidth}
                tileRadius={6}
                tileBottomSpace={10}
                tileTopSpace={10}
                tileleftSpace={tileleftSpace}
              />
            </div>
          )
        })}
    </div>
  )
}

const AccordionWrapper = ({ children, contentType }) => {
  const handleClick = (e): void => {
    e.stopPropagation()
  }
  return (
    <div onClick={handleClick}>
      <Accordion
        titleIcon={<Icon icon="Stacks" size="small" version="v2" className="mr-8" />}
        title={
          <Truncate isResponsive truncateFrom="end">
            {contentType.label}
          </Truncate>
        }
        isContainerization={true}
        isAccordionParent={true}
        renderExpanded={true}
        version={'v2'}
        className="SearchBar__customField__field">
        {children}
      </Accordion>
    </div>
  )
}

const operatorByQueryType = {
  advTextInputLabel: 'contains',
  customFieldJsonLabel: 'contains',
  jsonRteTextLabel: 'contains',
  textInputLabel: 'contains',
  linkLabel: 'contains'
}

const operatorLabelByValue = {
  $eq: 'Matches',
  contains: 'Contains'
}

const RenderContentTypes = ({ contentType, handleClickOnField, searchText = '' }) => {
  const onClickField = (selectedOpt, queryType) => {
    const evaluatedOperator = operatorByQueryType[queryType] || '$eq'

    const { label, value } = contentType

    const operator = [{ label: operatorLabelByValue[evaluatedOperator], value: evaluatedOperator }]

    const fieldQuery = {
      key: [selectedOpt],
      operator,
      value: [],
      queryType,
      contentType: {
        label,
        value
      }
    }
    handleClickOnField(fieldQuery)
  }

  const ctHasLength = Array.isArray(contentType.schema) && contentType.schema.length > 0

  if (searchText.length) {
    return (
      <AccordionWrapper contentType={contentType}>
        {ctHasLength
          ? contentType.schema.map((eachSchema, index) => {
            if (eachSchema.match) {
              return (
                <div key={index} className="SearchBar__customField__SearchFields">
                  {eachSchema.options.map((opt, index) => (
                    <div
                      tabIndex={0}
                      className="SearchBar__customField__field__item"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClickField(opt, eachSchema.queryType)
                      }}
                      key={index}
                      onKeyDown={(e) => {
                        if (e.keyCode == 32) {
                          e.preventDefault()
                          e.stopPropagation()
                          onClickField(opt, eachSchema.queryType)
                        }
                      }}
                      >
                      <div>
                        <div>
                          <Truncate isResponsive truncateFrom="end">
                            {opt.label}
                          </Truncate>
                        </div>
                        <div>
                          <Truncate isResponsive truncateFrom="end">
                            {opt.info}
                          </Truncate>
                        </div>
                      </div>
                      <Tooltip position="left" content={opt.icon.split('-')[1]}>
                        <Icon icon={opt.icon.split('-')[0]} size="small" version="v2" />
                      </Tooltip>
                    </div>
                  ))}
                </div>
              )
            }
            return null
          })
          : ''}
      </AccordionWrapper>
    )
  }

  return (
    <AccordionWrapper contentType={contentType}>
      {ctHasLength
        ? contentType.schema.map((eachSchema, index) => (
          <div key={index}>
            {eachSchema.group && (
              <div className="SearchBar__customField__field__item SearchBar__customField__field__group">
                <div style={{ marginLeft: eachSchema.nestingLevel ? `${eachSchema.nestingLevel * 14}px` : '' }}>
                  <Truncate isResponsive truncateFrom="end">
                    {eachSchema.group}
                  </Truncate>
                </div>
                <Tooltip position="left" content={eachSchema.icon.split('-')[1]}>
                  <Icon icon={eachSchema.icon.split('-')[0]} size="small" version="v2" />
                </Tooltip>
              </div>
            )}
            {eachSchema.options.map((opt, index) => (
              <div
                tabIndex={0}
                className="SearchBar__customField__field__item"
                onClick={(e) => {
                  e.stopPropagation()
                  onClickField(opt, eachSchema.queryType)
                }}
                key={index}
                onKeyDown={(e) => {
                  if (e.keyCode == 32) {
                    e.preventDefault()
                    e.stopPropagation()
                    onClickField(opt, eachSchema.queryType)
                  }
                }}
                >
                <div style={{ marginLeft: opt.nestingLevel ? `${opt.nestingLevel * 14}px` : '' }}>
                  <Truncate isResponsive truncateFrom="end">
                    {opt.label}
                  </Truncate>
                </div>
                <Tooltip position="left" content={opt.icon.split('-')[1]}>
                  <Icon icon={opt.icon.split('-')[0]} size="small" version="v2" />
                </Tooltip>
              </div>
            ))}
          </div>
        ))
        : ''}
    </AccordionWrapper>
  )
}

const limitToFetch = 10
const CustomField = (props) => {
  const { fetchData } = props

  let [contentTypesData, setCtData] = useState([])
  let [searchText, setSearchText] = useState('')
  let [isLoading, setIsLoading] = useState(false)
  let [paginationData, setPaginationData] = useState({ totalCounts: null, hasMore: true, skip: 0 })

  useEffect(() => {
    fetchInitData()
  }, [])

  const fetchInitData = async () => {
    try {
      setIsLoading(true)
      let formattedCtData = []
      const { data, count } = await fetchData.fetchContentTypesFieldSearch({
        skip: 0,
        limit: limitToFetch,
        searchText
      })

      data.forEach((eachCt) => {
        const userDefinedFields = getCtUserDefinedFields(eachCt.schema, [], '', '', searchText)
        formattedCtData.push({ label: eachCt.title, value: eachCt.uid, schema: userDefinedFields })
      })
      setCtData(formattedCtData)
      setIsLoading(false)
      setPaginationData({ totalCounts: count, hasMore: data.length >= count ? false : true, skip: 0 })
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  const handleClickOnField = (fieldData) => {
    props.getFieldsData(fieldData)
    setTimeout(() => {
      props.closeModal()
    }, 0)
  }

  const debounceSearch = async (searchText) => {
    try {
      setIsLoading(true)
      let formattedCtData = []
      const { data, count } = await fetchData.fetchContentTypesFieldSearch({ skip: 0, limit: limitToFetch, searchText })
      data.forEach((eachCt) => {
        const userDefinedFields = getCtUserDefinedFields(eachCt.schema, [], '', '', searchText)
        formattedCtData.push({ label: eachCt.title, value: eachCt.uid, schema: userDefinedFields })
      })
      setCtData(formattedCtData)
      setIsLoading(false)
      setPaginationData({ totalCounts: count, hasMore: data.length >= count ? false : true, skip: 0 })
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  const fetchOnScroll = async () => {
    try {
      const { hasMore, skip, totalCounts } = paginationData

      if (contentTypesData.length >= paginationData.totalCounts) {
        setPaginationData({ skip, totalCounts, hasMore: false })
        return
      }
      let formattedCtData = []
      const { data, count } = await fetchData.fetchContentTypesFieldSearch({
        skip: skip + limitToFetch,
        limit: limitToFetch,
        searchText
      })
      data.forEach((eachCt) => {
        const userDefinedFields = getCtUserDefinedFields(eachCt.schema, [], '', '', searchText)
        formattedCtData.push({ label: eachCt.title, value: eachCt.uid, schema: userDefinedFields })
      })

      setCtData([...contentTypesData, ...formattedCtData])
      setIsLoading(false)
      setPaginationData({ totalCounts: count, hasMore, skip: skip + limitToFetch })
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  const handleInputChange = (searchText) => {
    setSearchText(searchText)
    debounceSearch(searchText)
  }

  const emptyStateString = contentTypesData.length === 0 && (searchText ? 'No matching result found' : 'No Data')

  // console.log('data', contentTypesData)
  return (
    <div className="SearchBar__customField__modal">
      <ModalHeader title="Select specific field" closeModal={props.closeModal} />
      <ModalBody>
        <div className="SearchBar__customField__modal_inlineSearchWrapper">
          <Search
            testId={`cs-custom-field-search`}
            placeholder="Search"
            onChange={handleInputChange}
            width="auto"
            autoFocus={true}
            onClear={true}
            type="secondary"
            version="v2"
            debounceSearch={true}
          />
        </div>
        <div className="SearchBar__customField__fields" id="searchCustomFieldsScrollableDiv">
          <InfiniteScroll
            dataLength={contentTypesData.length}
            next={fetchOnScroll}
            hasMore={paginationData.hasMore}
            loader={!isLoading ? <SkeletonLoader /> : ''}
            scrollableTarget={'searchCustomFieldsScrollableDiv'}>
            {isLoading ? (
              <div style={{ height: '396px' }}>
                <LoaderV2 />
              </div>
            ) : !contentTypesData.length || paginationData.totalCounts === 0 ? (
              <div className="SearchBar__customField__field__item--empty flex-center">{emptyStateString}</div>
            ) : (
              contentTypesData.map((eachCt, index) => (
                <RenderContentTypes
                  contentType={eachCt}
                  handleClickOnField={handleClickOnField}
                  searchText={searchText}
                  key={index}
                />
              ))
            )}
          </InfiniteScroll>
        </div>
      </ModalBody>
    </div>
  )
}

export default CustomField
